import React, { useState, useEffect } from 'react';
import { Container, Modal, Tab, Tabs, Button, Table, Form, Alert, Spinner } from 'react-bootstrap';
import { API_URL } from '../../utils/constants';
import './SetupGuestsScreen.css';

function SetupGuestsScreen() {
  const [showModal, setShowModal] = useState(true);
  const [activeTab, setActiveTab] = useState('configuration');

  // Guest Type state
  const [guestTypes, setGuestTypes] = useState([]);
  const [loadingGuestTypes, setLoadingGuestTypes] = useState(true);
  const [savingGuestType, setSavingGuestType] = useState(false);
  
  // Guest Type Modal state
  const [showGuestTypeModal, setShowGuestTypeModal] = useState(false);
  const [guestTypeModalMode, setGuestTypeModalMode] = useState('add'); // 'add' or 'edit'
  const [selectedGuestType, setSelectedGuestType] = useState(null);
  const [guestTypeForm, setGuestTypeForm] = useState({ name: '', active: true, sort_order: 0 });
  const [guestTypeErrors, setGuestTypeErrors] = useState({});

  // Error and success messages
  const [message, setMessage] = useState({ text: '', variant: '', show: false });

  // Fetch data on component mount
  useEffect(() => {
    fetchGuestTypes();
  }, []);

  // API Functions for Guest Types
  const fetchGuestTypes = async () => {
    try {
      setLoadingGuestTypes(true);
      const response = await fetch(`${API_URL}/guest-types`);
      if (!response.ok) throw new Error('Failed to fetch guest types');
      const data = await response.json();
      setGuestTypes(data);
    } catch (error) {
      console.error('Error fetching guest types:', error);
      setMessage({ text: 'Failed to load guest types', variant: 'danger', show: true });
    } finally {
      setLoadingGuestTypes(false);
    }
  };

  const updateGuestType = async (id, updates) => {
    try {
      const response = await fetch(`${API_URL}/guest-types/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update guest type');
      }
      
      const updatedType = await response.json();
      setGuestTypes(guestTypes.map(gt => gt.id === id ? updatedType : gt));
      setMessage({ text: 'Guest type updated successfully', variant: 'success', show: true });
    } catch (error) {
      console.error('Error updating guest type:', error);
      setMessage({ text: error.message, variant: 'danger', show: true });
    }
  };

  const deleteGuestType = async (id) => {
    if (!window.confirm('Are you sure you want to delete this guest type?')) return;
    
    try {
      const response = await fetch(`${API_URL}/guest-types/${id}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete guest type');
      }
      
      setGuestTypes(guestTypes.filter(gt => gt.id !== id));
      setMessage({ text: 'Guest type deleted successfully', variant: 'success', show: true });
    } catch (error) {
      console.error('Error deleting guest type:', error);
      setMessage({ text: error.message, variant: 'danger', show: true });
    }
  };

  // Guest Type Modal handlers
  const handleAddGuestType = () => {
    setGuestTypeModalMode('add');
    setSelectedGuestType(null);
    setGuestTypeForm({ name: '', active: true, sort_order: 0 });
    setGuestTypeErrors({});
    setShowGuestTypeModal(true);
  };

  const handleEditGuestType = (guestType) => {
    setGuestTypeModalMode('edit');
    setSelectedGuestType(guestType);
    setGuestTypeForm({ name: guestType.name, active: guestType.active, sort_order: guestType.sort_order || 0 });
    setGuestTypeErrors({});
    setShowGuestTypeModal(true);
  };

  const handleGuestTypeInputChange = (field, value) => {
    setGuestTypeForm(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (guestTypeErrors[field]) {
      setGuestTypeErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateGuestTypeForm = () => {
    const errors = {};
    if (!guestTypeForm.name.trim()) {
      errors.name = 'Name is required';
    }
    return errors;
  };

  const handleSaveGuestType = async () => {
    const errors = validateGuestTypeForm();
    if (Object.keys(errors).length > 0) {
      setGuestTypeErrors(errors);
      return;
    }

    try {
      setSavingGuestType(true);
      if (guestTypeModalMode === 'add') {
        const response = await fetch(`${API_URL}/guest-types`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(guestTypeForm)
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to create guest type');
        }
        
        const newType = await response.json();
        setGuestTypes([...guestTypes, newType]);
        setMessage({ text: 'Guest type created successfully', variant: 'success', show: true });
      } else {
        await updateGuestType(selectedGuestType.id, guestTypeForm);
      }
      
      setShowGuestTypeModal(false);
      setGuestTypeForm({ name: '', active: true, sort_order: 0 });
    } catch (error) {
      setMessage({ text: error.message, variant: 'danger', show: true });
    } finally {
      setSavingGuestType(false);
    }
  };

  const handleCloseGuestTypeModal = () => {
    setShowGuestTypeModal(false);
    setGuestTypeForm({ name: '', active: true, sort_order: 0 });
    setGuestTypeErrors({});
  };

  const handleCloseModal = () => {
    // Modal can only be closed by clicking the X button or specific buttons
    // This prevents accidental closing
  };

  const handleModalClose = () => {
    setShowModal(false);
    setActiveTab('configuration');
  };

  const handleSaveAndClose = () => {
    // Save any changes and close modal
    setShowModal(false);
    setActiveTab('configuration');
  };

  const handleSave = () => {
    // Save any changes but keep modal open
    console.log('Saving changes...');
  };

  // Auto-hide messages after 5 seconds
  useEffect(() => {
    if (message.show) {
      const timer = setTimeout(() => {
        setMessage({ text: '', variant: '', show: false });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message.show]);

  return (
    <Container fluid className="setup-guests-screen">
      <div className="screen-header">
        <h1>Setup - Guests</h1>
        <p>Configure guest settings and management</p>
      </div>

      <div className="screen-content">
        {/* Message Alert */}
        {message.show && (
          <Alert 
            variant={message.variant} 
            dismissible 
            onClose={() => setMessage({ text: '', variant: '', show: false })}
            className="mb-3"
          >
            {message.text}
          </Alert>
        )}

        {/* Main Setup Modal */}
        <Modal 
          show={showModal} 
          onHide={handleCloseModal}
          size="xl"
          backdrop="static"
          keyboard={false}
          className="setup-modal"
        >
          <Modal.Header>
            <Modal.Title>Setup - Guests</Modal.Title>
            <Button
              variant="link"
              onClick={handleModalClose}
              className="close-button"
            >
              ×
            </Button>
          </Modal.Header>
          
          <Modal.Body>
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
              className="setup-tabs"
            >
              <Tab eventKey="configuration" title="Configuration">
                <div className="tab-content">
                  <div className="tab-header">
                    <h4>Configuration</h4>
                    <p>General guest configuration settings will be added here.</p>
                  </div>
                </div>
              </Tab>

              <Tab eventKey="guest-type" title="Guest Type">
                <div className="tab-content">
                  <div className="tab-header">
                    <h4>Guest Types</h4>
                    <Button 
                      variant="success" 
                      onClick={handleAddGuestType}
                      size="sm"
                    >
                      ➕ Add Guest Type
                    </Button>
                  </div>
                  
                  {loadingGuestTypes ? (
                    <div className="text-center py-4">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <Table striped bordered hover>
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Sort Order</th>
                            <th>Active</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {guestTypes.map((gt) => (
                            <tr key={gt.id}>
                              <td>{gt.name}</td>
                              <td>{gt.sort_order || 0}</td>
                              <td>
                                <span className={`badge ${gt.active ? 'bg-success' : 'bg-secondary'}`}>
                                  {gt.active ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => handleEditGuestType(gt)}
                                  className="me-2"
                                >
                                  ✏️ Edit
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => deleteGuestType(gt.id)}
                                >
                                  🗑️ Delete
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </div>
              </Tab>
            </Tabs>
          </Modal.Body>
          
          <Modal.Footer>
            <Button onClick={handleSaveAndClose}>
              Save & Close
            </Button>
            <Button onClick={handleSave}>
              Save
            </Button>
            <Button onClick={handleModalClose}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Guest Type Modal */}
        <Modal 
          show={showGuestTypeModal} 
          onHide={handleCloseGuestTypeModal}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {guestTypeModalMode === 'add' ? 'Add Guest Type' : 'Edit Guest Type'}
            </Modal.Title>
          </Modal.Header>
          
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Name *</Form.Label>
                <Form.Control
                  type="text"
                  value={guestTypeForm.name}
                  onChange={(e) => handleGuestTypeInputChange('name', e.target.value)}
                  isInvalid={!!guestTypeErrors.name}
                  placeholder="e.g., Regular Guest, VIP Guest, Corporate Guest"
                />
                <Form.Control.Feedback type="invalid">
                  {guestTypeErrors.name}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Sort Order</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={guestTypeForm.sort_order}
                  onChange={(e) => handleGuestTypeInputChange('sort_order', parseInt(e.target.value) || 0)}
                  placeholder="0"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Active"
                  checked={guestTypeForm.active}
                  onChange={(e) => handleGuestTypeInputChange('active', e.target.checked)}
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseGuestTypeModal}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSaveGuestType}
              disabled={savingGuestType}
            >
              {savingGuestType ? 'Saving...' : 'Save'}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </Container>
  );
}

export default SetupGuestsScreen;
